/**
 * Attachment Uploader Component
 *
 * Multi-file upload component for study attachments.
 * Supports drag & drop, type selection per file, and file preview.
 *
 * Allowed file types: PDF, JPG, PNG, JPEG
 */

import { useState, useRef, useCallback, useMemo } from 'react'
import {
  Upload,
  FileText,
  Image,
  X,
  AlertCircle,
  Paperclip,
  Check,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/presentation/components/ui/Button'
import { Select } from '@/presentation/components/ui/Select'
import {
  ATTACHMENT_TYPES,
  ATTACHMENT_TYPE_LABELS,
  type AttachmentType,
} from '@/config/constants'
import type { CreateAttachmentData } from '@/domain/attachment/types'

// =============================================================================
// Types
// =============================================================================

interface AttachmentFile {
  id: string
  file: File
  attachmentType: AttachmentType
  description: string
  base64Data: string
  previewUrl?: string
}

interface AttachmentUploaderProps {
  /** Current list of attachments */
  attachments: AttachmentFile[]
  /** Callback when attachments change */
  onChange: (attachments: AttachmentFile[]) => void
  /** Minimum number of attachments required */
  minRequired?: number
  /** Maximum number of attachments allowed */
  maxAllowed?: number
  /** Maximum file size in MB */
  maxFileSizeMB?: number
  /** Show error state */
  error?: string | null
  /** Disable the uploader */
  disabled?: boolean
  /** Additional CSS classes */
  className?: string
}

// =============================================================================
// Constants
// =============================================================================

const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
]

const ALLOWED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png']

/**
 * Tipo que se le declara al servidor.
 *
 * Hacen falta dos correcciones sobre `file.type`, y las dos se descubrieron
 * al volver obligatoria la validacion en el servidor:
 *
 * 1. El navegador deja `file.type` VACIO cuando no reconoce la extension.
 *    Aqui el archivo ya se acepto por su extension, asi que enviarlo vacio
 *    hacia fallar una subida que era legitima.
 * 2. `image/jpg` no existe como tipo. El nombre real es `image/jpeg`, y es
 *    el que el servidor conoce.
 *
 * El servidor comprueba ademas los primeros bytes del archivo, asi que esto
 * no es la validacion: es decir la verdad.
 */
const POR_EXTENSION: Record<string, string> = {
  '.pdf': 'application/pdf',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.png': 'image/png',
}

function resolverMimeType(file: File): string {
  if (file.type === 'image/jpg') return 'image/jpeg'
  if (ALLOWED_MIME_TYPES.includes(file.type)) return file.type

  const extension = Object.keys(POR_EXTENSION).find((ext) =>
    file.name.toLowerCase().endsWith(ext)
  )
  return extension ? POR_EXTENSION[extension]! : file.type
}

const FILE_TYPE_ICONS: Record<string, typeof FileText> = {
  'application/pdf': FileText,
  'image/jpeg': Image,
  'image/jpg': Image,
  'image/png': Image,
}

// =============================================================================
// Helpers
// =============================================================================

/**
 * Generate unique ID
 */
function generateId(): string {
  return `attachment_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

/**
 * Convert file to base64
 */
async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result as string
      // Remove the data URL prefix (e.g., "data:application/pdf;base64,")
      const base64 = result.split(',')[1]
      resolve(base64)
    }
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

/**
 * Format file size for display
 */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/**
 * Validate file type and size
 */
function validateFile(
  file: File,
  maxFileSizeMB: number
): { valid: boolean; error?: string } {
  // Check file type
  const isValidType = ALLOWED_MIME_TYPES.includes(resolverMimeType(file))
  const hasValidExtension = ALLOWED_EXTENSIONS.some((ext) =>
    file.name.toLowerCase().endsWith(ext)
  )

  if (!isValidType && !hasValidExtension) {
    return {
      valid: false,
      error: `Tipo de archivo no permitido: ${file.name}. Use PDF, JPG o PNG.`,
    }
  }

  // Check file size
  const maxBytes = maxFileSizeMB * 1024 * 1024
  if (file.size > maxBytes) {
    return {
      valid: false,
      error: `Archivo demasiado grande: ${file.name} (${formatFileSize(file.size)}). Máximo ${maxFileSizeMB}MB.`,
    }
  }

  return { valid: true }
}

// =============================================================================
// Component
// =============================================================================

export function AttachmentUploader({
  attachments,
  onChange,
  minRequired = 0,
  maxAllowed = 10,
  maxFileSizeMB = 10,
  error,
  disabled = false,
  className,
}: AttachmentUploaderProps) {
  const [isDragOver, setIsDragOver] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Attachment type options
  const attachmentTypeOptions = useMemo(
    () =>
      Object.entries(ATTACHMENT_TYPE_LABELS).map(([value, label]) => ({
        value,
        label,
      })),
    []
  )

  // Handlers
  const processFiles = useCallback(
    async (files: FileList | File[]) => {
      setValidationError(null)
      const fileArray = Array.from(files)

      // Check max limit
      const totalCount = attachments.length + fileArray.length
      if (totalCount > maxAllowed) {
        setValidationError(
          `Máximo ${maxAllowed} archivos permitidos. Ya tiene ${attachments.length}.`
        )
        return
      }

      const newAttachments: AttachmentFile[] = []

      for (const file of fileArray) {
        // Validate
        const validation = validateFile(file, maxFileSizeMB)
        if (!validation.valid) {
          setValidationError(validation.error || 'Archivo inválido')
          continue
        }

        try {
          // Convert to base64
          const base64Data = await fileToBase64(file)

          // Create preview URL for images
          let previewUrl: string | undefined
          if (file.type.startsWith('image/')) {
            previewUrl = URL.createObjectURL(file)
          }

          newAttachments.push({
            id: generateId(),
            file,
            // La orden medica es el soporte que se adjunta casi siempre, y
            // es el que la IPS necesita identificar para radicar la factura.
            // Con OTHER por defecto, todo entraba clasificado como "Otro" y
            // habia que corregir el desplegable en cada carga.
            attachmentType: ATTACHMENT_TYPES.MEDICAL_ORDER,
            description: '',
            base64Data,
            previewUrl,
          })
        } catch {
          setValidationError(`Error al procesar: ${file.name}`)
        }
      }

      if (newAttachments.length > 0) {
        onChange([...attachments, ...newAttachments])
      }
    },
    [attachments, onChange, maxAllowed, maxFileSizeMB]
  )

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        processFiles(e.target.files)
      }
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    },
    [processFiles]
  )

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragOver(false)

      if (disabled) return

      if (e.dataTransfer.files) {
        processFiles(e.dataTransfer.files)
      }
    },
    [processFiles, disabled]
  )

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      if (!disabled) {
        setIsDragOver(true)
      }
    },
    [disabled]
  )

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }, [])

  const handleRemove = useCallback(
    (id: string) => {
      const attachment = attachments.find((a) => a.id === id)
      if (attachment?.previewUrl) {
        URL.revokeObjectURL(attachment.previewUrl)
      }
      onChange(attachments.filter((a) => a.id !== id))
    },
    [attachments, onChange]
  )

  const handleTypeChange = useCallback(
    (id: string, type: AttachmentType) => {
      onChange(
        attachments.map((a) => (a.id === id ? { ...a, attachmentType: type } : a))
      )
    },
    [attachments, onChange]
  )

  const handleDescriptionChange = useCallback(
    (id: string, description: string) => {
      onChange(
        attachments.map((a) => (a.id === id ? { ...a, description } : a))
      )
    },
    [attachments, onChange]
  )

  // Display error
  const displayError = error || validationError
  const hasMinimumAttachments = attachments.length >= minRequired

  return (
    <div className={cn('space-y-3', className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-[var(--foreground)]">
          <Paperclip className="inline h-4 w-4 mr-1" />
          Adjuntos
          {minRequired > 0 && (
            <span className="text-red-500"> * (mínimo {minRequired})</span>
          )}
        </label>
        <span className="text-xs text-[var(--foreground-muted)]">
          {attachments.length}/{maxAllowed} archivos
        </span>
      </div>

      {/* Error message */}
      {displayError && (
        <div className="flex items-center gap-2 p-2 rounded-md bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span className="text-xs">{displayError}</span>
        </div>
      )}

      {/* Drop zone */}
      {attachments.length < maxAllowed && (
        <div
          className={cn(
            'border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors',
            isDragOver
              ? 'border-[var(--primary)] bg-[var(--primary)]/5'
              : 'border-[var(--border)] hover:border-[var(--primary)]/50 hover:bg-[var(--background-subtle)]',
            disabled && 'opacity-50 cursor-not-allowed'
          )}
          onClick={() => !disabled && fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <Upload
            className={cn(
              'h-6 w-6 mx-auto mb-2',
              isDragOver ? 'text-[var(--primary)]' : 'text-[var(--foreground-muted)]'
            )}
          />
          <p className="text-sm text-[var(--foreground)]">
            Arrastre archivos aquí o haga clic
          </p>
          <p className="text-xs text-[var(--foreground-muted)] mt-1">
            PDF, JPG, PNG (máx. {maxFileSizeMB}MB c/u)
          </p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ALLOWED_EXTENSIONS.join(',')}
            onChange={handleFileSelect}
            disabled={disabled}
            className="hidden"
          />
        </div>
      )}

      {/* Attachment list */}
      {attachments.length > 0 && (
        <div className="space-y-2">
          {attachments.map((attachment) => {
            const IconComponent =
              FILE_TYPE_ICONS[attachment.file.type] || FileText

            return (
              <div
                key={attachment.id}
                className="flex items-start gap-3 p-3 rounded-md bg-[var(--background-subtle)] border border-[var(--border)]"
              >
                {/* Preview/Icon */}
                <div className="flex-shrink-0">
                  {attachment.previewUrl ? (
                    <img
                      src={attachment.previewUrl}
                      alt={attachment.file.name}
                      className="h-12 w-12 object-cover rounded"
                    />
                  ) : (
                    <div className="h-12 w-12 flex items-center justify-center bg-[var(--background)] rounded">
                      <IconComponent className="h-6 w-6 text-[var(--foreground-muted)]" />
                    </div>
                  )}
                </div>

                {/* Details */}
                <div className="flex-1 min-w-0 space-y-2">
                  {/* File name and size */}
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-[var(--foreground)] truncate">
                      {attachment.file.name}
                    </p>
                    <span className="text-xs text-[var(--foreground-muted)] ml-2 flex-shrink-0">
                      {formatFileSize(attachment.file.size)}
                    </span>
                  </div>

                  {/* Type selector */}
                  <Select
                    options={attachmentTypeOptions}
                    value={attachment.attachmentType}
                    onChange={(e) =>
                      handleTypeChange(attachment.id, e.target.value as AttachmentType)
                    }
                    disabled={disabled}
                    className="text-xs h-8"
                  />

                  {/* Description input */}
                  <input
                    type="text"
                    placeholder="Descripción (opcional)"
                    value={attachment.description}
                    onChange={(e) =>
                      handleDescriptionChange(attachment.id, e.target.value)
                    }
                    disabled={disabled}
                    className="w-full h-7 text-xs rounded border border-[var(--border)] bg-[var(--background)] px-2"
                  />
                </div>

                {/* Remove button */}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemove(attachment.id)}
                  disabled={disabled}
                  className="flex-shrink-0"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )
          })}
        </div>
      )}

      {/* Minimum requirement status */}
      {minRequired > 0 && (
        <div
          className={cn(
            'flex items-center gap-1 text-xs',
            hasMinimumAttachments
              ? 'text-green-600 dark:text-green-400'
              : 'text-[var(--foreground-muted)]'
          )}
        >
          {hasMinimumAttachments ? (
            <Check className="h-3 w-3" />
          ) : (
            <AlertCircle className="h-3 w-3" />
          )}
          <span>
            {hasMinimumAttachments
              ? 'Requisito mínimo cumplido'
              : `Agregue al menos ${minRequired - attachments.length} archivo(s) más`}
          </span>
        </div>
      )}
    </div>
  )
}

// =============================================================================
// Export utilities
// =============================================================================

export type { AttachmentFile }

/**
 * Convert AttachmentFile array to CreateAttachmentData array for API submission
 */
export function prepareAttachmentsForUpload(
  attachments: AttachmentFile[]
): CreateAttachmentData[] {
  return attachments.map((a) => ({
    attachmentType: a.attachmentType,
    fileName: a.file.name,
    fileData: a.base64Data,
    mimeType: resolverMimeType(a.file),
    description: a.description || undefined,
  }))
}
